import { clientLogger } from '../utils/clientLogger';
import { useEffect, useRef, useState } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { authApi, devicesApi } from '@/api';
import { useAppStore, useAuthStore } from '@/hooks/useStore';
import {
  PUSH_PREFERENCE_EVENT,
  readPushPreference,
  type PushPreferenceChange,
  writePushPreference,
} from '@/utils/pushPreferences';

interface PushPreferenceState {
  userId: string;
  enabled: boolean | null;
}

interface PushRegistrationRetryOptions {
  retryDelayMs?: number;
  maxRetryDelayMs?: number;
}

let deviceLifecycleQueue: Promise<void> = Promise.resolve();

function enqueueDeviceLifecycle<T>(operation: () => Promise<T>): Promise<T> {
  const result = deviceLifecycleQueue.then(operation, operation);
  deviceLifecycleQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

export function usePushNotifications(
  navigate: (path: string) => void,
  retryOptions: PushRegistrationRetryOptions = {},
) {
  const { isAuthenticated, token: authorizationToken, user } = useAuthStore();
  const setPushDeviceToken = useAppStore((state) => state.setPushDeviceToken);
  const [preference, setPreference] = useState<PushPreferenceState | null>(null);
  const navigateRef = useRef(navigate);
  const pushEnabled = preference && preference.userId === user?.id ? preference.enabled : null;
  const retryDelayMs = Math.max(0, retryOptions.retryDelayMs ?? 1_000);
  const maxRetryDelayMs = Math.max(retryDelayMs, retryOptions.maxRetryDelayMs ?? 30_000);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const handlePreferenceChange = (event: Event) => {
      const detail = (event as CustomEvent<PushPreferenceChange>).detail;
      if (!detail || detail.userId !== useAuthStore.getState().user?.id) return;
      setPreference({ userId: detail.userId, enabled: detail.enabled });
    };
    window.addEventListener(PUSH_PREFERENCE_EVENT, handlePreferenceChange);
    return () => window.removeEventListener(PUSH_PREFERENCE_EVENT, handlePreferenceChange);
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated || !authorizationToken || !user?.id) {
      setPreference(null);
      return;
    }

    const userId = user.id;
    const capturedToken = authorizationToken;
    const cachedPreference = readPushPreference(userId);
    let active = true;

    // Do not register while the server preference is unknown. This prevents a
    // newly signed-in opted-out account from receiving a push before Settings
    // has ever been opened on this device.
    setPreference({ userId, enabled: null });

    authApi
      .getNotificationSettings(capturedToken)
      .then((settings) => {
        const currentAuth = useAuthStore.getState();
        if (!active || currentAuth.user?.id !== userId || currentAuth.token !== capturedToken) {
          return;
        }

        const enabled = settings.browser_enabled !== false;
        writePushPreference(userId, enabled);
        setPreference({ userId, enabled });
      })
      .catch(() => {
        const currentAuth = useAuthStore.getState();
        if (active && currentAuth.user?.id === userId && currentAuth.token === capturedToken) {
          // A same-user, server-confirmed cache is a safe offline fallback. A
          // fresh device with no cache remains opted out until synchronization.
          setPreference({ userId, enabled: cachedPreference });
        }
      });

    return () => {
      active = false;
    };
  }, [authorizationToken, isAuthenticated, user?.id]);

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      !isAuthenticated ||
      !authorizationToken ||
      !user?.id ||
      pushEnabled !== true
    ) {
      return;
    }

    let mounted = true;
    let registeredToken: string | null = null;
    let activeListenerHandles: PluginListenerHandle[] = [];
    let deactivateActiveAttempt: (() => void) | null = null;
    let setupComplete = false;
    let failureCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingAttempt: Promise<void> | null = null;

    const removeHandles = async (handles: PluginListenerHandle[]): Promise<void> => {
      await Promise.allSettled(handles.map((handle) => handle.remove()));
    };

    const scheduleRetry = (error: unknown) => {
      if (!mounted || retryTimer) return;

      failureCount += 1;
      const delay = Math.min(retryDelayMs * 2 ** (failureCount - 1), maxRetryDelayMs);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attemptSetup();
      }, delay);
      clientLogger.error('Push setup error; retrying:', error);
    };

    const setupAttempt = async (): Promise<{
      handles: PluginListenerHandle[];
      deactivate: () => void;
      isActive: () => boolean;
    }> => {
      let attemptActive = true;
      const attemptHandles: PluginListenerHandle[] = [];
      const deactivate = () => {
        attemptActive = false;
      };
      const isActive = () => attemptActive;
      const retryAttempt = (error: unknown) => {
        if (!mounted || !attemptActive) return;
        deactivate();
        if (deactivateActiveAttempt === deactivate) {
          deactivateActiveAttempt = null;
          activeListenerHandles = [];
          setupComplete = false;
        }
        void removeHandles(attemptHandles).then(() => scheduleRetry(error));
      };
      const retainListener = async (
        pendingHandle: Promise<PluginListenerHandle>,
      ): Promise<boolean> => {
        const handle = await pendingHandle;
        if (!mounted || !attemptActive) {
          await handle.remove().catch(() => undefined);
          return false;
        }
        attemptHandles.push(handle);
        return true;
      };

      try {
        // Check / request permission
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') return { handles: [], deactivate, isActive };

        // Listen for registration success
        if (
          !(await retainListener(
            PushNotifications.addListener('registration', async (token) => {
              if (!mounted || !attemptActive) return;
              try {
                await enqueueDeviceLifecycle(async () => {
                  if (!mounted || !attemptActive) return;
                  // Serialize ownership changes for the physical APNs token. A
                  // delayed request from account A must settle and clean up before
                  // account B can claim the same device.
                  await devicesApi.register(token.value, 'ios', authorizationToken);
                  const currentAuth = useAuthStore.getState();
                  if (
                    !mounted ||
                    !attemptActive ||
                    currentAuth.user?.id !== user.id ||
                    currentAuth.token !== authorizationToken
                  ) {
                    await devicesApi
                      .unregister(token.value, authorizationToken)
                      .catch(() => undefined);
                    return;
                  }
                  registeredToken = token.value;
                  setPushDeviceToken(token.value);
                  // Registration is complete only when the server can deliver
                  // to this device. Keep backoff across server/network failures.
                  failureCount = 0;
                });
              } catch (err) {
                // APNs succeeding does not mean the backend retained its token.
                // Retire this attempt and retry so a temporary API outage does
                // not silently disable push until the app is relaunched.
                retryAttempt(err);
              }
            }),
          ))
        ) {
          return { handles: attemptHandles, deactivate, isActive };
        }

        // Listen for registration errors
        if (
          !(await retainListener(
            PushNotifications.addListener('registrationError', (err) => {
              if (!mounted || !attemptActive) return;

              // Capacitor's iOS register() call resolves immediately; APNs
              // reports asynchronous failures through this event. Tear down
              // this exact attempt before retrying so a transient native error
              // cannot leave setup permanently complete or duplicate listeners.
              retryAttempt(err);
            }),
          ))
        ) {
          return { handles: attemptHandles, deactivate, isActive };
        }

        // Handle notification taps (app opened from notification)
        if (
          !(await retainListener(
            PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
              if (!attemptActive) return;
              const link = notification.notification.data?.link as string | undefined;
              if (link) {
                navigateRef.current(link);
              }
            }),
          ))
        ) {
          return { handles: attemptHandles, deactivate, isActive };
        }

        // Register with APNs
        if (mounted && attemptActive) {
          await PushNotifications.register();
        }
        return { handles: attemptHandles, deactivate, isActive };
      } catch (error) {
        deactivate();
        await removeHandles(attemptHandles);
        throw error;
      }
    };

    const attemptSetup = () => {
      if (!mounted || setupComplete || pendingAttempt || retryTimer) return;

      const operation = { promise: Promise.resolve() };
      operation.promise = setupAttempt()
        .then(async ({ handles, deactivate, isActive }) => {
          if (!mounted) {
            deactivate();
            await removeHandles(handles);
            return;
          }
          // A very fast native registrationError can arrive before this promise
          // continuation. Its callback already owns teardown and retry.
          if (!isActive()) return;
          activeListenerHandles = handles;
          deactivateActiveAttempt = deactivate;
          setupComplete = true;
        })
        .catch((error: unknown) => {
          scheduleRetry(error);
        })
        .finally(() => {
          if (pendingAttempt === operation.promise) pendingAttempt = null;
        });
      pendingAttempt = operation.promise;
    };

    attemptSetup();

    return () => {
      mounted = false;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      deactivateActiveAttempt?.();
      deactivateActiveAttempt = null;
      const handlesToRemove = activeListenerHandles;
      activeListenerHandles = [];
      void removeHandles(handlesToRemove);
      if (registeredToken) {
        const tokenToUnregister = registeredToken;
        void enqueueDeviceLifecycle(() =>
          devicesApi.unregister(tokenToUnregister, authorizationToken),
        )
          .then(() => {
            const currentUser = useAuthStore.getState().user;
            if (
              (!currentUser || currentUser.id === user.id) &&
              useAppStore.getState().pushDeviceToken === tokenToUnregister
            ) {
              setPushDeviceToken(null);
            }
          })
          .catch((err) => {
            clientLogger.error('Failed to unregister device token:', err);
          });
      }
    };
  }, [
    authorizationToken,
    isAuthenticated,
    maxRetryDelayMs,
    pushEnabled,
    retryDelayMs,
    setPushDeviceToken,
    user?.id,
  ]);
}
